import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { portalApi } from '@/api/portal'
import { FilterBar, FilterField } from '@/components/filter-bar'
import { Frame } from '@/components/frame'
import { PageHeader } from '@/components/page-header'
import { PortalTaskList } from '@/components/portal/portal-task-list'
import { SearchInput } from '@/components/search-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { msg } from '@/i18n'
import {
  REQUEST_STATE_LABELS,
  REQUEST_STATE_SLUGS,
  REQUEST_STATE_STATUSES,
  REQUEST_STATES,
  requestStateFromSlug,
} from '@/lib/requests'
import { matching, oneOf, readSearch, text } from '@/lib/search-params'

const ALL = '__tous__'

const SHAPE = {
  q: text(),
  /** #331 : un état lisible par le client, jamais un statut interne. */
  etat: oneOf(REQUEST_STATES.map((state) => REQUEST_STATE_SLUGS[state])),
  /** 1.50.0 : les tâches d'un projet — le bloc « tâches » d'un projet y mène. */
  projet: matching(/^[0-9a-f]{24}$/),
}

export const Route = createFileRoute('/portal/tasks/')({
  validateSearch: (search: Record<string, unknown>) => readSearch(SHAPE, search),
  component: PortalTasksPage,
})

function PortalTasksPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const t = msg().portalRequests.list
  const [query, setQuery] = useState(search.q ?? '')
  const debouncedQuery = useDebouncedValue(query)

  useEffect(() => {
    const current = search.q ?? ''
    if (debouncedQuery !== current) {
      void navigate({
        search: (previous) => ({ ...previous, q: debouncedQuery || undefined }),
        replace: true,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- piloté par la saisie
  }, [debouncedQuery])

  const filtered =
    search.q !== undefined || search.etat !== undefined || search.projet !== undefined
  const state = requestStateFromSlug(search.etat)
  const params = {
    q: search.q ?? undefined,
    status: state ? REQUEST_STATE_STATUSES[state] : undefined,
    project_id: search.projet,
    page_size: 100,
  }
  // Le nom du projet, pour sa puce : la liste des projets du client, déjà servie.
  const projects = useQuery({
    queryKey: ['portal', 'projects'],
    queryFn: () => portalApi.projects(),
    enabled: search.projet !== undefined,
  })
  const projectName = projects.data?.find((project) => project.id === search.projet)?.name
  const tasks = useQuery({
    queryKey: ['portal', 'tasks', params],
    queryFn: () => portalApi.tasks(params),
    placeholderData: (previous) => previous,
  })

  return (
    <div className="space-y-5">
      <PageHeader title={t.title} />

      <Frame>
        <FilterBar
          search={
            <SearchInput
              value={query}
              onChange={setQuery}
              label={t.searchLabel}
              placeholder={t.searchPlaceholder}
              className="w-full max-w-xs"
            />
          }
          active={[
            ...(search.projet
              ? [
                  {
                    key: 'projet',
                    label: t.projectChip(projectName ?? ''),
                    onClear: () => {
                      void navigate({
                        search: (previous) => ({ ...previous, projet: undefined }),
                        replace: true,
                      })
                    },
                  },
                ]
              : []),
            ...(state
              ? [
                  {
                    key: 'etat',
                    label: REQUEST_STATE_LABELS[state],
                    onClear: () => {
                      void navigate({
                        search: (previous) => ({ ...previous, etat: undefined }),
                        replace: true,
                      })
                    },
                  },
                ]
              : []),
          ]}
          panelTitle={t.panelTitle}
        >
          <FilterField label={t.state}>
            <Select
              value={search.etat ?? ALL}
              onValueChange={(value) => {
                void navigate({
                  search: (previous) => ({
                    ...previous,
                    etat: value === ALL ? undefined : value,
                  }),
                  replace: true,
                })
              }}
            >
              <SelectTrigger aria-label={t.stateFilter}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t.allStates}</SelectItem>
                {REQUEST_STATES.map((value) => (
                  <SelectItem key={value} value={REQUEST_STATE_SLUGS[value]}>
                    {REQUEST_STATE_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
        </FilterBar>

        <PortalTaskList
          tasks={tasks.data?.items ?? []}
          isPending={tasks.isPending}
          filtered={filtered}
          emptyTitle={filtered ? t.emptyFiltered : t.empty}
          emptyDescription={filtered ? t.emptyFilteredHint : undefined}
        />
      </Frame>
    </div>
  )
}
